import React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@utils/cn';

export type BudgetItemTaskGroupVariant = 'task' | 'delegated' | 'line-items';

export interface BudgetItemTaskGroupHeaderProps {
  title: string;
  itemCount: number;
  subtotal: number;
  /** When omitted, header renders static (no chevron, not clickable) — useful in read-only views like preview/email. */
  isExpanded?: boolean;
  onToggle?: () => void;
  variant?: BudgetItemTaskGroupVariant;
  className?: string;
}

const formatCurrency = (value: number): string => {
  return `$ ${value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
};

const VARIANT_BADGE: Record<BudgetItemTaskGroupVariant, { label: string; classes: string } | null> = {
  task: null,
  delegated: { label: 'Delegated task', classes: 'bg-orange-100 text-orange-700' },
  'line-items': { label: 'Project line items', classes: 'bg-purple-100 text-purple-700' },
};

export const BudgetItemTaskGroupHeader: React.FC<BudgetItemTaskGroupHeaderProps> = ({
  title,
  itemCount,
  subtotal,
  isExpanded,
  onToggle,
  variant = 'task',
  className,
}) => {
  const isInteractive = typeof onToggle === 'function';
  const Chevron = isExpanded ? ChevronDown : ChevronRight;
  const badge = VARIANT_BADGE[variant];

  const body = (
    <>
      <div className="flex items-center gap-2 min-w-0">
        {isInteractive && <Chevron className="w-4 h-4 text-gray-500 flex-shrink-0" />}
        <span className="text-sm font-medium text-gray-900 truncate">{title}</span>
        {badge && (
          <span
            className={cn(
              'inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] whitespace-nowrap',
              badge.classes,
            )}>
            {badge.label}
          </span>
        )}
      </div>
      <div className="flex items-center gap-3 text-xs text-gray-600 flex-shrink-0">
        <span>
          {itemCount} item{itemCount === 1 ? '' : 's'}
        </span>
        <span className="font-medium text-gray-900">{formatCurrency(subtotal)}</span>
      </div>
    </>
  );

  const baseClasses = cn(
    'w-full flex items-center justify-between gap-3 px-3 py-2 text-left',
    'bg-gray-50 border-y border-gray-200',
    isInteractive && 'hover:bg-gray-100',
    className,
  );

  if (!isInteractive) {
    return <div className={baseClasses}>{body}</div>;
  }

  return (
    <button type="button" onClick={onToggle} aria-expanded={isExpanded} className={baseClasses}>
      {body}
    </button>
  );
};
